import { useId } from 'react'
import { cn } from './cn'
import {
  MARK_ASPECT,
  MARK_CYAN,
  MARK_DASH,
  MARK_G_LINE,
  MARK_G_NODE,
  MARK_G_PATH,
  MARK_M_LINE,
  MARK_M_PATH,
  MARK_RED,
  MARK_VIEWBOX,
} from './logo-data'

export interface LogoMarkProps {
  /** Rendered height in px (width follows the mark aspect ratio). */
  height?: number
  /** Soft glow around the mark; off by default (REDESIGN section 4: no glows on product surfaces). */
  glow?: boolean
  className?: string
  title?: string
}

/** The MG logomark: cyan M, red G and dashed circuit underlines (optional soft glow). */
export function LogoMark({ height = 28, glow = false, className, title }: LogoMarkProps) {
  const uid = useId().replace(/:/g, '')
  const filterId = `mgs-mark-glow-${uid}`
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      height={height}
      width={Math.round(height * MARK_ASPECT)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={cn('shrink-0', className)}
    >
      {glow && (
        <defs>
          <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%" colorInterpolationFilters="sRGB">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.6" result="blur" />
            <feComponentTransfer in="blur" result="soft">
              <feFuncA type="linear" slope="0.35" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode in="soft" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}
      <g filter={glow ? `url(#${filterId})` : undefined}>
        <path d={MARK_M_PATH} fill={MARK_CYAN} />
        <path d={MARK_G_PATH} fill="none" stroke={MARK_RED} strokeWidth="10" strokeLinejoin="round" />
        <path d={MARK_M_LINE} stroke={MARK_CYAN} strokeWidth="3.2" strokeDasharray={MARK_DASH} />
        <path d={MARK_G_LINE} stroke={MARK_RED} strokeWidth="3.2" strokeDasharray={MARK_DASH} />
        <circle {...MARK_G_NODE} fill="none" stroke={MARK_RED} strokeWidth="2" />
      </g>
    </svg>
  )
}

export interface LogoProps {
  /** Mark only (no "AI School" wordmark). */
  compact?: boolean
  /** Mark height in px (default 28). */
  height?: number
  className?: string
}

/**
 * Wordmark: the MG logomark + "AI School" (AI in accent, School in text colour). MG (2026-10-06): the mark already says
 * MG, so the text no longer repeats it; the accessible name stays "MG AI School".
 */
export function Logo({ compact = false, height = 28, className }: LogoProps) {
  if (compact) return <LogoMark height={height} title="MG AI School" className={className} />
  const fontSize = Math.round(height * 0.643)
  return (
    <span className={cn('inline-flex items-center gap-2 select-none', className)} aria-label="MG AI School" role="img">
      <LogoMark height={height} />
      <span className="font-extrabold tracking-[-0.02em] whitespace-nowrap" style={{ fontSize, lineHeight: 1 }} aria-hidden="true">
        <span className="text-accent-strong dark:text-accent">AI</span> <span className="text-text">School</span>
      </span>
    </span>
  )
}
